/**
 * Market Segment Derivation Utility
 *
 * Automatically classifies the client into a market segment based on
 * revenue band and employee count from Business Scoping responses.
 *
 * The market segment is NOT a profile selector — it's a modifier that
 * adjusts size-dependent parameters on top of the industry threat profile.
 *
 * Segments:
 *   - small-market:       Revenue < $25M  OR  headcount < 51
 *   - mid-market:         Revenue $25M–$250M  AND  headcount 51–250
 *   - upper-mid-market:   Revenue > $250M  OR  headcount > 250
 *
 * When revenue and headcount imply different segments, use the HIGHER
 * segment (more conservative from a risk perspective).
 */

// Revenue band mappings from bs-1
// Maps to segment based on businessScoping.json options (mid-market focused)
const REVENUE_BAND_SEGMENTS = {
  'Under $25M': 'small-market',
  '$25M \u2013 $50M': 'mid-market',
  '$50M \u2013 $100M': 'mid-market',
  '$100M \u2013 $250M': 'mid-market',
  '$250M \u2013 $500M': 'upper-mid-market',
  'Over $500M': 'upper-mid-market',
  // Legacy mappings for backwards compatibility with existing assessments
  '<$25mm': 'small-market',
  '$25mm - $50mm': 'mid-market',
  '$50mm - $100mm': 'mid-market',
  '$100mm - $250mm': 'mid-market',
  '$250mm - $500mm': 'upper-mid-market',
  '>$500mm': 'upper-mid-market',
  '<$5mm': 'small-market',
  '$5mm - $25mm': 'small-market',
  '$25mm - $100mm': 'mid-market',
  '>$100mm': 'upper-mid-market'
}

// Employee count mappings from bs-3
// Note: Uses en-dash (–) characters as in businessScoping.json
const EMPLOYEE_BAND_SEGMENTS = {
  '1–15': 'small-market',
  '16–50': 'small-market',
  '51–250': 'mid-market',
  '250+': 'upper-mid-market'
}

// Segment ranking for conflict resolution (higher = larger organization)
const SEGMENT_RANK = {
  'small-market': 1,
  'mid-market': 2,
  'upper-mid-market': 3
}

/**
 * All size-dependent modifiers live here — single source of truth.
 * These values are tuned based on industry data and client engagement feedback.
 *
 * @param {string} segment - The market segment ID
 * @returns {Object} Complete modifier set for the segment
 */
export function getSegmentModifiers(segment) {
  const modifiers = {
    'small-market': {
      // Loss calculation parameters
      downtimeDays: { low: 2, likely: 5, high: 14 },
      costPerRecord: { low: 150, likely: 200, high: 350 },
      directLossMultiplier: { low: 0.005, likely: 0.015, high: 0.04 },
      recoveryCost: { low: 15000, likely: 50000, high: 150000 },

      // Threat context
      threatActorSophistication: 'opportunistic',
      threatActorDescription: 'Primarily targeted by opportunistic and automated attacks. Lower likelihood of dedicated, persistent threat actors but high exposure to commodity malware, phishing campaigns, and credential-based attacks.',

      // Regulatory
      regulatoryScrutinyLevel: 'standard',
      regulatoryNote: 'Subject to standard regulatory oversight. Lower likelihood of proactive regulatory audit, but breach notification and compliance obligations remain fully applicable.',

      // Roadmap
      initiativeCostMultiplier: 0.65,

      // Narrative
      narrativeModifier: 'As a smaller regulated organization, resource constraints mean security investments must be highly targeted. The threat landscape is dominated by opportunistic attacks, but regulatory obligations demand the same compliance rigor as larger peers.'
    },

    'mid-market': {
      downtimeDays: { low: 3, likely: 7, high: 21 },
      costPerRecord: { low: 165, likely: 250, high: 425 },
      directLossMultiplier: { low: 0.005, likely: 0.015, high: 0.04 },
      recoveryCost: { low: 35000, likely: 100000, high: 350000 },

      threatActorSophistication: 'targeted-opportunistic',
      threatActorDescription: 'Faces a mix of opportunistic and semi-targeted attacks. Large enough to appear in threat actor targeting criteria (revenue, data volume, industry) but typically not subject to dedicated APT campaigns.',

      regulatoryScrutinyLevel: 'elevated',
      regulatoryNote: 'Increased regulatory visibility. More likely to face scheduled compliance audits and proactive regulatory engagement. Breach events draw meaningful regulatory attention.',

      initiativeCostMultiplier: 1.0,

      narrativeModifier: 'As a mid-market organization, you face a sophisticated threat landscape that includes both commodity attacks and targeted campaigns. Your organization is large enough to attract deliberate attention while potentially lacking the dedicated security resources of larger enterprises.'
    },

    'upper-mid-market': {
      downtimeDays: { low: 5, likely: 10, high: 30 },
      costPerRecord: { low: 180, likely: 300, high: 500 },
      directLossMultiplier: { low: 0.003, likely: 0.012, high: 0.035 },
      recoveryCost: { low: 75000, likely: 250000, high: 750000 },

      threatActorSophistication: 'targeted',
      threatActorDescription: 'Likely targeted by sophisticated threat actors including organized cybercrime groups with industry-specific tooling. Revenue and data scale make this organization a high-value target.',

      regulatoryScrutinyLevel: 'high',
      regulatoryNote: 'High regulatory visibility. Subject to proactive compliance examinations. Breach events will draw significant regulatory scrutiny, potential enforcement action, and media attention.',

      initiativeCostMultiplier: 1.5,

      narrativeModifier: 'As an upper mid-market organization, you represent a high-value target for sophisticated threat actors. The scale of operations, data volumes, and regulatory exposure demand enterprise-grade security capabilities with dedicated program leadership.'
    }
  }

  return modifiers[segment] || modifiers['mid-market']
}

/**
 * Segment display labels
 */
const SEGMENT_LABELS = {
  'small-market': 'Small Market',
  'mid-market': 'Mid-Market',
  'upper-mid-market': 'Upper Mid-Market'
}

/**
 * Full segment definitions with display info
 */
export const MARKET_SEGMENTS = {
  'small-market': {
    id: 'small-market',
    name: 'Small Market',
    displayName: 'Small Market Organization',
    description: 'Organizations with limited resources and simpler infrastructure, typically <$25M revenue or <50 employees',
    revenueRange: '<$25M',
    employeeRange: '<50',
    benchmarkTier: 'small-market'
  },
  'mid-market': {
    id: 'mid-market',
    name: 'Mid-Market',
    displayName: 'Mid-Market Organization',
    description: 'Organizations with established processes and dedicated IT/security resources, typically $25M-$250M revenue or 50-250 employees',
    revenueRange: '$25M - $250M',
    employeeRange: '50-250',
    benchmarkTier: 'mid-market'
  },
  'upper-mid-market': {
    id: 'upper-mid-market',
    name: 'Upper Mid-Market',
    displayName: 'Upper Mid-Market Organization',
    description: 'Larger organizations with complex infrastructure and mature security programs, typically >$250M revenue or >250 employees',
    revenueRange: '$250M - $500M+',
    employeeRange: '>250',
    benchmarkTier: 'upper-mid-market'
  }
}

/**
 * Derive market segment from business scoping responses.
 * Uses the HIGHER of revenue-based or employee-based segment (more conservative).
 *
 * @param {Object} businessScopingResponses - The business scoping responses object
 * @returns {Object} Full derived segment object including modifiers and derivation details
 */
export function deriveMarketSegment(businessScopingResponses) {
  if (!businessScopingResponses) {
    return {
      segment: 'mid-market',
      segmentLabel: 'Mid-Market',
      ...MARKET_SEGMENTS['mid-market'],
      derivedFrom: {
        revenueBand: null,
        revenueImpliedSegment: null,
        headcountBand: null,
        headcountImpliedSegment: null,
        effectiveRule: 'Default (no data available)'
      },
      modifiers: getSegmentModifiers('mid-market')
    }
  }

  // Extract responses - handle both string and object response formats
  const revenueResponse = businessScopingResponses['bs-1']
  const employeeResponse = businessScopingResponses['bs-3']

  const revenueValue = typeof revenueResponse === 'object' ? revenueResponse?.value : revenueResponse
  const employeeValue = typeof employeeResponse === 'object' ? employeeResponse?.value : employeeResponse

  // Map to segments
  const revenueSegment = REVENUE_BAND_SEGMENTS[revenueValue] || null
  const employeeSegment = EMPLOYEE_BAND_SEGMENTS[employeeValue] || null

  // Default to mid-market if data is missing
  const revenueRank = revenueSegment ? SEGMENT_RANK[revenueSegment] : 2
  const headcountRank = employeeSegment ? SEGMENT_RANK[employeeSegment] : 2

  // Use the HIGHER of the two (more conservative)
  const effectiveRank = Math.max(revenueRank, headcountRank)
  const segment = Object.entries(SEGMENT_RANK)
    .find(([, rank]) => rank === effectiveRank)?.[0] || 'mid-market'

  // Determine which rule applied
  let effectiveRule = 'Revenue and headcount agree'
  if (!revenueSegment && !employeeSegment) {
    effectiveRule = 'Default (no data available)'
  } else if (!revenueSegment) {
    effectiveRule = 'Derived from headcount only'
  } else if (!employeeSegment) {
    effectiveRule = 'Derived from revenue only'
  } else if (revenueRank > headcountRank) {
    effectiveRule = 'Revenue implies higher segment'
  } else if (headcountRank > revenueRank) {
    effectiveRule = 'Headcount implies higher segment'
  }

  return {
    segment,
    segmentLabel: SEGMENT_LABELS[segment] || 'Mid-Market',
    ...MARKET_SEGMENTS[segment],
    derivedFrom: {
      revenueBand: revenueValue || null,
      revenueImpliedSegment: revenueSegment || null,
      headcountBand: employeeValue || null,
      headcountImpliedSegment: employeeSegment || null,
      effectiveRule
    },
    modifiers: getSegmentModifiers(segment)
  }
}

/**
 * Gets just the segment ID string from business scoping responses.
 *
 * @param {Object} businessScopingResponses - The business scoping responses object
 * @returns {string} The segment ID (e.g., 'small-market', 'mid-market', 'upper-mid-market')
 */
export function getMarketSegmentId(businessScopingResponses) {
  return deriveMarketSegment(businessScopingResponses).segment
}

/**
 * Gets the initiative cost modifier for the segment.
 *
 * @param {Object} businessScopingResponses - The business scoping responses object
 * @returns {number} Cost modifier (0.65 - 1.5)
 */
export function getCostModifier(businessScopingResponses) {
  return deriveMarketSegment(businessScopingResponses).modifiers.initiativeCostMultiplier
}

/**
 * Gets the display label for showing segment in UI.
 *
 * @param {Object} businessScopingResponses - The business scoping responses object
 * @returns {string} Display name (e.g., "Mid-Market Organization")
 */
export function getMarketSegmentDisplayName(businessScopingResponses) {
  return deriveMarketSegment(businessScopingResponses).displayName
}

/**
 * Gets the segment label for badge display.
 *
 * @param {Object} businessScopingResponses - The business scoping responses object
 * @returns {string} Segment label (e.g., "Mid-Market")
 */
export function getMarketSegmentLabel(businessScopingResponses) {
  return deriveMarketSegment(businessScopingResponses).segmentLabel
}

/**
 * Validates that revenue and employee responses are reasonably consistent.
 * Flags potential data quality issues if responses differ by more than one tier.
 *
 * @param {Object} businessScopingResponses - The business scoping responses object
 * @returns {Object} Validation result with isConsistent and message
 */
export function validateSegmentConsistency(businessScopingResponses) {
  if (!businessScopingResponses) {
    return { isConsistent: true, message: null }
  }

  const revenueResponse = businessScopingResponses['bs-1']
  const employeeResponse = businessScopingResponses['bs-3']

  const revenueValue = typeof revenueResponse === 'object' ? revenueResponse?.value : revenueResponse
  const employeeValue = typeof employeeResponse === 'object' ? employeeResponse?.value : employeeResponse

  const revenueSegment = REVENUE_BAND_SEGMENTS[revenueValue]
  const employeeSegment = EMPLOYEE_BAND_SEGMENTS[employeeValue]

  if (!revenueSegment || !employeeSegment) {
    return { isConsistent: true, message: null }
  }

  const revenuePriority = SEGMENT_RANK[revenueSegment]
  const employeePriority = SEGMENT_RANK[employeeSegment]
  const diff = Math.abs(revenuePriority - employeePriority)

  if (diff > 1) {
    return {
      isConsistent: false,
      message: `Revenue band (${revenueValue}) and employee count (${employeeValue}) suggest significantly different organization sizes. Using the larger segment for assessment.`
    }
  }

  return { isConsistent: true, message: null }
}

/**
 * Get all segment modifiers for loss calculation.
 * Convenience function for impactCalibration.js integration.
 *
 * @param {Object} businessScopingResponses - The business scoping responses object
 * @returns {Object} Full modifiers object
 */
export function getSegmentModifiersFromResponses(businessScopingResponses) {
  return deriveMarketSegment(businessScopingResponses).modifiers
}

export default {
  deriveMarketSegment,
  getMarketSegmentId,
  getCostModifier,
  getMarketSegmentDisplayName,
  getMarketSegmentLabel,
  validateSegmentConsistency,
  getSegmentModifiers,
  getSegmentModifiersFromResponses,
  MARKET_SEGMENTS
}
